import {
  useAppSelector,
  useAppDispatch,
  setDeliveryError,
  setScheduleSlots,
  setOrderInfoLoading,
  setFulfillmentToTime,
  setFulfillmentToTimeError,
} from '@/app/store';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { generateTimeIntervals, useLazyGetGeocodeQuery, useGetOptimalDeliveryZoneMutation } from '@/entities';
import { getLocalizedReduxErrorMessage, parseAddressFromGeocoder } from '@/shared/lib';
import { useChangePersonalInfoDelivery, getOrderItemsFromMenuItems } from '@/features';

export const useActualiseOptimalDeliveryZone = (isOrderAnotherPerson?: boolean) => {
  const dispatch = useAppDispatch();
  const { fulfillmentToTime } = useAppSelector((state) => state.order);
  const { totalCheck, products, unavailableProductIds } = useAppSelector((state) => state.basket);

  const { delivery, onSetDeliveryZone, onSetNationwideInfo, onClearDeliveryZone } = useChangePersonalInfoDelivery(
    isOrderAnotherPerson ?? false,
  );

  const orderedItems = useMemo(
    () => getOrderItemsFromMenuItems(products, unavailableProductIds),
    [products, unavailableProductIds],
  );

  const [debounceTotalCheck] = useDebounce(totalCheck, 1000);

  const { t } = useTranslation('validation');
  const { t: tErrors } = useTranslation('errors');
  const { t: tBasket } = useTranslation('basket');

  const [getOptimalDeliveryZone] = useGetOptimalDeliveryZoneMutation();
  const [triggerGeocode] = useLazyGetGeocodeQuery();

  const actualiseOptimalDZ = useCallback(async () => {
    if (delivery.coords && totalCheck !== 0) {
      dispatch(setOrderInfoLoading(true));
      try {
        const zone = await getOptimalDeliveryZone({
          getOptimalDeliveryZoneDto: {
            orderPrice: totalCheck,
            point: delivery.coords,
            date: fulfillmentToTime,
            timeSlotsAdjustmentInfo: { orderedItems },
          },
        }).unwrap();

        if (zone.data) {
          if (zone.data.nationwideInfo) {
            const nwLocation = zone.data.nationwideInfo.location;
            const nwDeliveryPrice = zone.data.nationwideInfo.deliveryPrice;
            const geocodeData = await triggerGeocode({
              geocode: `${nwLocation.coordinates.lng},${nwLocation.coordinates.lat}`,
            }).unwrap();
            const geocodeObject = geocodeData.response.GeoObjectCollection.featureMember[0].GeoObject;
            const { city } = parseAddressFromGeocoder(geocodeObject);

            dispatch(setDeliveryError(undefined));

            onSetDeliveryZone({
              price: undefined,
              zoneId: undefined,
              locationId: undefined,
              paymentTypes: nwLocation.delivery?.paymentTypes ?? [],
              onlinePayment: nwLocation.delivery?.onlinePayment ?? undefined,
            });

            onSetNationwideInfo({ locationId: nwLocation.id, city, price: nwDeliveryPrice });
          } else {
            onSetNationwideInfo(undefined);
            const mergedSchedule = zone.data.mergedSchedule;

            if (mergedSchedule.dailyTimeslots.length === 0) {
              dispatch(setScheduleSlots([]));
              if (!zone.data.mergedSchedule.asapAvailable) dispatch(setDeliveryError(t('delivery.zoneUnavailable')));
              else dispatch(setDeliveryError(undefined));
            } else {
              dispatch(setDeliveryError(undefined));
              dispatch(
                setScheduleSlots(
                  generateTimeIntervals(mergedSchedule.dailyTimeslots, mergedSchedule.asapAvailable, tBasket) ?? [],
                ),
              );
            }

            if (zone.data.deliveryZone) {
              dispatch(setFulfillmentToTimeError(undefined));

              onSetDeliveryZone({
                zoneId: zone.data.deliveryZone.deliveryZone.id,
                locationId: zone.data.deliveryZone.location.id,
                price: zone.data.deliveryZone.deliveryPrice ?? 0,
                paymentTypes: zone.data.deliveryZone.availablePaymentTypes,
                onlinePayment: zone.data.deliveryZone.location.delivery?.onlinePayment ?? undefined,
              });
            } else {
              onClearDeliveryZone();
              if (mergedSchedule.dailyTimeslots.length > 0)
                dispatch(setFulfillmentToTimeError(t('fulfillment.dateTimeRequired')));
              else dispatch(setFulfillmentToTimeError(t('fulfillment.whenClosed')));
            }
          }
        }
      } catch (error) {
        onClearDeliveryZone();
        onSetNationwideInfo(undefined);
        dispatch(setFulfillmentToTime(undefined));
        dispatch(setDeliveryError(t('delivery.zoneUnavailable')));
        toast.error(getLocalizedReduxErrorMessage(error ?? '', tErrors));
      } finally {
        dispatch(setOrderInfoLoading(false));
      }
    }
  }, [
    t,
    tErrors,
    tBasket,
    dispatch,
    totalCheck,
    orderedItems,
    delivery.coords,
    fulfillmentToTime,
    onSetDeliveryZone,
    onSetNationwideInfo,
    onClearDeliveryZone,
    getOptimalDeliveryZone,
    triggerGeocode,
  ]);

  useEffect(() => {
    actualiseOptimalDZ();
  }, [actualiseOptimalDZ, debounceTotalCheck, isOrderAnotherPerson]);
};
